// Age-band vitals assessment — shared by the Prescription vitals panel and the Token Queue
// chips so both colour a reading the same way: neutral when in range, a severity colour only
// when abnormal. Advisory only — precise interpretation belongs in the clinical views.

// DOB → age band. NEWBORN (0-28d) · INFANT (1-12m) · YOUNG_CHILD (1-5y) · CHILD (5-12y) ·
// ADOLESCENT (12-18y, adult-equivalent ranges but labelled "adolescent" so it doesn't read as
// "adult" next to the legal Minor badge) · ADULT (18+).
import { toClinicDate, clinicNow } from './clinicTime';
import { ageInYears } from './age';

export const getAgeBandKey = (dob) => {
  if (!dob) return 'ADULT';
  // dateOfBirth is a date-only value, which new Date() reads as UTC midnight — a day earlier
  // than intended west of UTC. On a band boundary that puts a patient in the wrong band, and
  // the band decides which vitals ranges a reading is judged against. 'now' is clinic time
  // for the same reason: near midnight the browser's day and the clinic's differ.
  const birth = toClinicDate(dob);
  const now = clinicNow();
  if (!birth || isNaN(birth.getTime())) return 'ADULT';
  const days = Math.floor((now - birth) / 86400000);
  if (days <= 28) return 'NEWBORN';
  const months = (now.getFullYear() - birth.getFullYear()) * 12
    + (now.getMonth() - birth.getMonth())
    - (now.getDate() < birth.getDate() ? 1 : 0);
  if (months < 12) return 'INFANT';
  // By the calendar, via the one shared implementation. This used to divide elapsed milliseconds
  // by an average year of 365.25 days, which made a patient 17.998 on the day they turned 18 and
  // judged them against ADOLESCENT ranges. See ageParts in utils/age.
  const years = ageInYears(dob);
  if (years < 5) return 'YOUNG_CHILD';
  if (years < 12) return 'CHILD';
  if (years < 18) return 'ADOLESCENT';
  return 'ADULT';
};

export const BAND_LABEL = {
  NEWBORN: 'newborn', INFANT: 'infant', YOUNG_CHILD: 'young child',
  CHILD: 'child', ADOLESCENT: 'adolescent', ADULT: 'adult',
};

// Per-band normal ranges. Outside these we further classify as "mild" (slightly out) or
// "concern"/"alarm" (significantly out / clinically dangerous). Keyed by data field.
export const VITALS_RANGES = {
  NEWBORN:     { pulse: [110, 160], respiratoryRate: [40, 60], temperature: [97, 99], spo2: [95, 100] },
  INFANT:      { pulse: [100, 160], respiratoryRate: [30, 50], temperature: [97, 99], spo2: [95, 100] },
  YOUNG_CHILD: { pulse: [90, 140],  respiratoryRate: [24, 40], temperature: [97, 99], spo2: [95, 100] },
  CHILD:       { pulse: [70, 110],  respiratoryRate: [18, 30], temperature: [97, 99], spo2: [95, 100], bpSys: [90, 120], bpDia: [55, 80] },
  // Adolescents (12-18) use adult-equivalent ranges clinically — kept identical to ADULT.
  ADOLESCENT:  { pulse: [60, 100],  respiratoryRate: [12, 20], temperature: [97, 99], spo2: [95, 100], bpSys: [90, 140], bpDia: [60, 90] },
  ADULT:       { pulse: [60, 100],  respiratoryRate: [12, 20], temperature: [97, 99], spo2: [95, 100], bpSys: [90, 140], bpDia: [60, 90] },
};

// Build a list of issues across the supplied vitals + band. Each issue carries its own
// severity (mild / concern / alarm); callers roll them up to a single banner colour.
export const assessVitals = (v, band) => {
  const ranges = VITALS_RANGES[band] || VITALS_RANGES.ADULT;
  const issues = [];

  const checkRange = (value, range, label, unit, severityIfMildOut = 'mild', severityIfFarOut = 'concern') => {
    if (value == null || value === '' || !range) return;
    const [low, high] = range;
    const num = Number(value);
    if (!Number.isFinite(num)) return;
    if (num >= low && num <= high) return; // normal
    const span = high - low;
    const farThreshold = span * 0.25; // 25% beyond the range edge → escalate
    const direction = num < low ? 'low' : 'high';
    const distance = num < low ? (low - num) : (num - high);
    const severity = distance > farThreshold ? severityIfFarOut : severityIfMildOut;
    issues.push({
      label,
      value: `${num}${unit ? ' ' + unit : ''}`,
      finding: `${direction === 'high' ? 'High' : 'Low'} (normal ${low}–${high}${unit ? ' ' + unit : ''})`,
      severity,
    });
  };

  checkRange(v.pulse,           ranges.pulse,            'Pulse',        'bpm',  'mild', 'concern');
  checkRange(v.respiratoryRate, ranges.respiratoryRate,  'Resp Rate',    '/min', 'mild', 'concern');
  checkRange(v.spo2,            ranges.spo2,             'SpO₂',         '%',    'concern', 'alarm');

  // Temperature has its own non-linear scale — fever escalates quickly, hypothermia is dangerous.
  if (v.temperature != null && v.temperature !== '') {
    const t = Number(v.temperature);
    if (Number.isFinite(t)) {
      if (t >= 103)        issues.push({ label: 'Temp', value: `${t} °F`, finding: 'High fever',        severity: 'alarm' });
      else if (t >= 100.4) issues.push({ label: 'Temp', value: `${t} °F`, finding: 'Fever',             severity: 'concern' });
      else if (t > 99)     issues.push({ label: 'Temp', value: `${t} °F`, finding: 'Slightly elevated', severity: 'mild' });
      else if (t < 95)     issues.push({ label: 'Temp', value: `${t} °F`, finding: 'Hypothermia',       severity: 'alarm' });
      else if (t < 96.8)   issues.push({ label: 'Temp', value: `${t} °F`, finding: 'Low',               severity: 'mild' });
    }
  }

  // BP — ranges with hypertensive crisis escalation (≥180/120 systolic) and hypotension.
  if (ranges.bpSys && v.bpSystolic != null && v.bpDiastolic != null && v.bpSystolic !== '' && v.bpDiastolic !== '') {
    const sys = Number(v.bpSystolic);
    const dia = Number(v.bpDiastolic);
    if (Number.isFinite(sys) && Number.isFinite(dia)) {
      if (sys >= 180 || dia >= 120) {
        issues.push({ label: 'BP', value: `${sys}/${dia} mmHg`, finding: 'Hypertensive crisis', severity: 'alarm' });
      } else if (sys >= 140 || dia >= 90) {
        issues.push({ label: 'BP', value: `${sys}/${dia} mmHg`, finding: 'High', severity: 'concern' });
      } else if (sys < ranges.bpSys[0] || dia < ranges.bpDia[0]) {
        const severity = (sys < ranges.bpSys[0] - 10 || dia < ranges.bpDia[0] - 10) ? 'concern' : 'mild';
        issues.push({ label: 'BP', value: `${sys}/${dia} mmHg`, finding: 'Low', severity });
      } else if (sys > 130 || dia > 80) {
        issues.push({ label: 'BP', value: `${sys}/${dia} mmHg`, finding: 'Elevated', severity: 'mild' });
      }
    }
  }

  // MUAC — WHO under-5 malnutrition criteria. Skip for older age bands.
  if ((band === 'INFANT' || band === 'YOUNG_CHILD') && v.muacCm != null && v.muacCm !== '') {
    const muac = Number(v.muacCm);
    if (Number.isFinite(muac)) {
      if (muac < 11.5)      issues.push({ label: 'MUAC', value: `${muac} cm`, finding: 'Severe acute malnutrition (WHO)', severity: 'alarm' });
      else if (muac < 12.5) issues.push({ label: 'MUAC', value: `${muac} cm`, finding: 'Moderate acute malnutrition',     severity: 'concern' });
    }
  }

  // Blood sugar — broad ranges since context varies (fasting vs random); only flag clearly
  // pathological values to avoid noise from a normal post-meal reading.
  if (v.bloodSugar != null && v.bloodSugar !== '') {
    const bs = Number(v.bloodSugar);
    if (Number.isFinite(bs)) {
      if (bs < 70)       issues.push({ label: 'Blood Sugar', value: `${bs} mg/dL`, finding: 'Hypoglycemia',  severity: bs < 54 ? 'alarm' : 'concern' });
      else if (bs > 200) issues.push({ label: 'Blood Sugar', value: `${bs} mg/dL`, finding: 'Hyperglycemia', severity: bs > 300 ? 'alarm' : 'concern' });
      else if (bs > 140) issues.push({ label: 'Blood Sugar', value: `${bs} mg/dL`, finding: 'Elevated',      severity: 'mild' });
    }
  }

  // Roll up to the overall worst severity.
  const order = { normal: 0, mild: 1, concern: 2, alarm: 3 };
  const worst = issues.reduce((acc, i) => order[i.severity] > order[acc] ? i.severity : acc, 'normal');
  return { issues, severity: worst };
};

// Map of chip-label → severity for the abnormal readings (handles the "Resp"/"Resp Rate" alias),
// computed directly from a DOB. Labels match the chip labels used in the panels.
const LABEL_ALIAS = { Resp: 'Resp Rate' };
export const vitalSeverityByLabel = (v, dob) => {
  const { issues } = assessVitals(v, getAgeBandKey(dob));
  const map = {};
  issues.forEach(iss => { map[iss.label] = iss.severity; });
  return (label) => map[label] || map[LABEL_ALIAS[label]] || 'normal';
};

// Severity → chip tint classes. Traffic-light code the user asked for:
//   green  = normal (in range)
//   amber  = borderline / slightly out of range ("mild")
//   red    = high / clearly abnormal ("concern" and "alarm")
export const vitalChipToneClass = (severity) => {
  switch (severity) {
    case 'alarm':   return 'bg-red-50 border-red-300 text-red-700';
    case 'concern': return 'bg-red-50 border-red-200 text-red-700';
    case 'mild':    return 'bg-amber-50 border-amber-200 text-amber-700';
    default:        return 'bg-emerald-50 border-emerald-200 text-emerald-700'; // normal / not assessed
  }
};

/**
 * BMI from a vitals reading: weight (kg) / height (m)^2.
 *
 * Height and length are the same physical measurement — length is simply how it is recorded for
 * under-2s, lying down — so either satisfies the formula and both are in cm. Returns null unless
 * both inputs are present, which is why a reading with no weight shows no BMI rather than a zero.
 *
 * Shared so the nurse's live figure while recording and the patient's history agree exactly.
 */
export const computeBmi = (reading) => {
  if (!reading) return null;
  const weight = parseFloat(reading.weight);
  const heightCm = parseFloat(reading.height || reading.lengthCm);
  if (!weight || !heightCm || weight <= 0 || heightCm <= 0) return null;
  const metres = heightCm / 100;
  return weight / (metres * metres);
};

/**
 * BMI rounded to the one decimal every surface displays it at, as a NUMBER — for the trend chart,
 * which plots numbers rather than strings and would otherwise render the raw 33.21799307958478.
 *
 * Round only for display. Never feed a rounded BMI to `bmiCategory`: a raw 24.96 would round to
 * 25.0 and flip a patient from Normal to Overweight on the boundary.
 */
export const roundBmi = (reading) => {
  const b = computeBmi(reading);
  return b == null ? null : Math.round(b * 10) / 10;
};

/** One decimal, as a string, or null. */
export const formatBmi = (reading) => {
  const b = roundBmi(reading);
  return b == null ? null : b.toFixed(1);
};
